// web/src/components/ui/Icon.tsx — the single icon primitive (M0.3). Every glyph
// in the UI flows through here so iconography stays consistent: one set
// (lucide-react), one sizing/stroke convention, token-driven colour. No emoji
// anywhere in the UI — import a named lucide icon and render <Icon icon={X} />.
//
// Colour inherits `currentColor` by default (so an icon takes the colour of its
// text/button context); pass `tone` to map to a semantic token, or wrap in an
// element with a token `color`. Size follows a small fixed scale to keep the
// optical weight even across the app. Decorative by default (aria-hidden);
// pass `title` to expose an accessible label.
import React from 'react';
import type { LucideIcon } from 'lucide-react';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type IconTone =
  | 'inherit'
  | 'accent'
  | 'muted'
  | 'subtle'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

const SIZE_PX: Record<IconSize, number> = {
  xs: 14,
  sm: 16,
  md: 18,
  lg: 22,
  xl: 28,
};

const TONE_VAR: Record<IconTone, string | undefined> = {
  inherit: undefined,
  accent: 'var(--color-accent)',
  muted: 'var(--color-text2)',
  subtle: 'var(--color-text3)',
  success: 'var(--color-green)',
  warning: 'var(--color-amber)',
  danger: 'var(--color-red)',
  info: 'var(--color-blue)',
};

export interface IconProps {
  /** A named lucide icon component, e.g. `import { Vault } from 'lucide-react'`. */
  icon: LucideIcon;
  size?: IconSize;
  /** Semantic colour token; defaults to `currentColor` (inherits context). */
  tone?: IconTone;
  /** Stroke width; lucide default is 2. Slightly thinner reads calmer at small sizes. */
  strokeWidth?: number;
  /** Accessible label. When set the icon is exposed to AT; otherwise decorative. */
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function Icon({
  icon: Glyph,
  size = 'md',
  tone = 'inherit',
  strokeWidth = 1.75,
  title,
  className,
  style,
}: IconProps): React.JSX.Element {
  const px = SIZE_PX[size];
  const color = TONE_VAR[tone];
  return (
    <Glyph
      width={px}
      height={px}
      strokeWidth={strokeWidth}
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      aria-label={title}
      style={{ color, flex: '0 0 auto', display: 'block', ...style }}
    />
  );
}

export default Icon;
